import { convertToReadableText } from '@acme/lib';
import { Button } from '@components/ui/button';
import {
  ChevronRight,
  Shirt,
  MapPin,
  Camera,
  Scissors,
  Layers,
  Palette,
  Image,
  HardHat,
  Glasses,
  Ruler,
  Eye,
  Activity,
  User,
  Circle,
  Drama,
  Plus,
  ShirtIcon
} from 'lucide-react';

type CategoryButtonProps = {
  icon?: React.ReactNode;
  category: string;
  categoryName?: string;
  selectedValues?: React.ReactNode[];
  isOptional?: boolean;
  onClick: () => void;
};

export function CategoryButton({
  icon,
  category,
  categoryName,
  selectedValues = [],
  isOptional = false,
  onClick
}: CategoryButtonProps) {
  // Icon resolution moved inside the component
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'upperClothing':
        return <ShirtIcon className="w-4 h-4 mr-2" />;
      case 'clothing':
        return <Shirt className="w-4 h-4 mr-2" />;
      case 'location':
        return <MapPin className="w-4 h-4 mr-2" />;
      case 'color':
        return <Palette className="w-4 h-4 mr-2" />;
      case 'photography':
        return <Image className="w-4 h-4 mr-2" aria-hidden="true" />;
      case 'scene':
        return <Layers className="w-4 h-4 mr-2" />;
      case 'camera':
        return <Camera className="w-4 h-4 mr-2" />;
      case 'hair':
        return <Scissors className="w-4 h-4 mr-2" />;
      case 'facialHair':
        return <User className="w-4 h-4 mr-2" />;
      case 'shirt':
        return <Shirt className="w-4 h-4 mr-2" />;
      case 'jacket':
        return <Shirt className="w-4 h-4 mr-2" />;
      case 'pants':
        return <Shirt className="w-4 h-4 mr-2" />;
      case 'shoes':
        return <Shirt className="w-4 h-4 mr-2" />;
      case 'glasses':
        return <Glasses className="w-4 h-4 mr-2" />;
      case 'outfit':
        return <Shirt className="w-4 h-4 mr-2" />;
      case 'bodyType':
        return <Activity className="w-4 h-4 mr-2" />;
      case 'eyeColor':
        return <Eye className="w-4 h-4 mr-2" />;
      case 'hairLength':
        return <Ruler className="w-4 h-4 mr-2" />;
      case 'shotType':
        return <Camera className="w-4 h-4 mr-2" />;
      case 'emotion':
        return <Drama className="w-4 h-4 mr-2" />;
      case 'hat':
        return <HardHat className="w-4 h-4 mr-2" />;
      case 'garment':
        return <Shirt className="w-4 h-4 mr-2" />;
      default:
        return <Circle className="w-4 h-4 mr-2" />;
    }
  };

  // Use the provided icon or determine it from the category
  const buttonIcon = icon || getCategoryIcon(category);
  // Use the provided categoryName or convert from category
  const displayName = categoryName || convertToReadableText(category);

  return (
    <Button
      type="button"
      onClick={onClick}
      className="w-full justify-between px-4 min-w-0 gap-4"
      variant="outline"
      size="xl"
    >
      <div className="flex items-center whitespace-nowrap w-1/3">
        {buttonIcon}
        <span className="text-left">{displayName}</span>
      </div>
      <div className="flex items-center min-w-0">
        {selectedValues.length > 0 ? (
          <>
            <div className="flex flex-col items-end ">{selectedValues}</div>
            <ChevronRight className="shrink-0 w-4 h-4 ml-2 text-muted-foreground" />
          </>
        ) : (
          <>
            <span className="text-sm text-muted-foreground">{isOptional ? 'Add' : 'Random'}</span>
            {isOptional ? (
              <Plus className="shrink-0 w-4 h-4 ml-2 text-muted-foreground" />
            ) : (
              <ChevronRight className="shrink-0 w-4 h-4 ml-2 text-muted-foreground" />
            )}
          </>
        )}
      </div>
    </Button>
  );
}
